"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useConfirm = void 0;
const react_1 = __importStar(require("react"));
const components_1 = require("@veeam/components");
const components_2 = require("../../components");
const core_1 = require("../../core");
require("../../core/request/types");
const utils_1 = require("../utils");
function useConfirm({ requestConfig: { url: requestUrl, method: requestMethod, ids: requestIds = () => [], payload: requestPayload, httpProxy: requestProxy, params: requestParams = {}, } = {}, needRequest = true, loading, message: initialMessage, width: initialWidth, header: initialHeader, iconSrc: initialIconSrc, messageWhiteSpace: initialMessageWhiteSpace, wordBreak: initialWordBreak = 'normal', silent: initialSilent = false, closeOnError: initialCloseOnError = false, doneActionLabel: initialDoneActionLabel, cancelActionLabel: initialCancelActionLabel, onDone, onCancel, } = {}) {
    const { getApiRequest, getErrorListener } = core_1.useAppContext();
    const [isLoading, updateLoad] = react_1.useState(false);
    const initialOptions = react_1.useMemo(() => {
        return {
            width: initialWidth,
            header: initialHeader,
            iconSrc: initialIconSrc,
            messageWhiteSpace: initialMessageWhiteSpace,
            wordBreak: initialWordBreak,
            silent: initialSilent,
            closeOnError: initialCloseOnError,
            doneActionLabel: initialDoneActionLabel,
            cancelActionLabel: initialCancelActionLabel,
        };
    }, []);
    const [message, setMessage] = react_1.useState(initialMessage);
    const [options, setOptions] = react_1.useState(initialOptions);
    const doneCallbackRef = react_1.useRef();
    const cancelCallbackRef = react_1.useRef();
    const proxy = requestProxy ||
        new core_1.HttpProxy({
            url: requestUrl,
            method: requestMethod,
        });
    const proxyRef = react_1.useRef(proxy);
    if (typeof proxyRef.current.setContext === 'function') {
        proxyRef.current.setContext(getApiRequest());
    }
    const { width, header, iconSrc, messageWhiteSpace, wordBreak, silent, closeOnError, doneActionLabel, cancelActionLabel, } = options;
    const handleDone = (onDeactivate) => {
        var _a;
        if (!needRequest) {
            onDeactivate();
            (_a = doneCallbackRef.current) === null || _a === void 0 ? void 0 : _a.call(doneCallbackRef);
            return;
        }
        updateLoad(true);
        let promiseResult;
        if (Array.isArray(requestIds()) && requestIds().length) {
            const promises = requestIds().map((id) => proxyRef.current.load(requestPayload, Object.assign(Object.assign({}, requestParams), { id })).toPromise());
            promiseResult = Promise.all(promises);
        }
        else {
            promiseResult = proxyRef.current.load(requestPayload, requestParams).toPromise();
        }
        promiseResult
            .then(() => {
            var _a;
            updateLoad(false);
            onDeactivate();
            (_a = doneCallbackRef.current) === null || _a === void 0 ? void 0 : _a.call(doneCallbackRef);
        })
            .catch((error) => {
            var _a;
            if (closeOnError) {
                onDeactivate();
                (_a = cancelCallbackRef.current) === null || _a === void 0 ? void 0 : _a.call(cancelCallbackRef);
            }
            updateLoad(false);
            getErrorListener().onCatchError(error);
        });
    };
    const handleClose = (onDeactivate) => {
        var _a;
        onDeactivate();
        (_a = cancelCallbackRef.current) === null || _a === void 0 ? void 0 : _a.call(cancelCallbackRef);
    };
    const [dialog, actions] = components_1.useModal({
        render: ({ deactivate }) => (react_1.default.createElement(components_2.ConfirmDialog, { deactivate: deactivate, loading: loading || isLoading, width: width, header: header, iconSrc: iconSrc, message: message, messageWhiteSpace: messageWhiteSpace, doneActionLabel: doneActionLabel, cancelActionLabel: cancelActionLabel, wordBreak: wordBreak, onDone: () => {
                handleDone(deactivate);
            }, onClose: () => {
                handleClose(deactivate);
            } })),
    });
    const showMessage = (messageValue, showConfirmOptions) => {
        const _a = showConfirmOptions || {}, { onDoneCallback, onCancelCallback } = _a, baseOptions = __rest(_a, ["onDoneCallback", "onCancelCallback"]);
        setMessage(typeof messageValue === 'string' ? messageValue : initialMessage);
        cancelCallbackRef.current = onCancelCallback !== null && onCancelCallback !== void 0 ? onCancelCallback : onCancel;
        doneCallbackRef.current = onDoneCallback !== null && onDoneCallback !== void 0 ? onDoneCallback : onDone;
        setOptions(Object.assign(Object.assign({}, initialOptions), baseOptions));
        actions.activate();
    };
    return [
        dialog,
        Object.assign(Object.assign({}, actions), { activate: (messageValue, showConfirmOptions) => {
                const { onDoneCallback, onCancelCallback } = showConfirmOptions || {};
                cancelCallbackRef.current = onCancelCallback !== null && onCancelCallback !== void 0 ? onCancelCallback : onCancel;
                doneCallbackRef.current = onDoneCallback !== null && onDoneCallback !== void 0 ? onDoneCallback : onDone;
                if (silent) {
                    handleDone(utils_1.noop);
                    return;
                }
                showMessage(messageValue, showConfirmOptions);
            } }),
        isLoading,
    ];
}
exports.useConfirm = useConfirm;
//# sourceMappingURL=useConfirm.js.map